import React from "react";
import { useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { activeUsersByAppVersionReport, activeUsersByAppVersionReportByRange } from "./graphql";
import { AnalyticsReportQuery } from "./analyticsTypes";
import {
  compareSortValues,
  normalizeDimensionValue,
  parseMetricValue,
} from "./analyticsUtils";

const COLORS = ["#1e88e5", "#43a047", "#fb8c00", "#8e24aa"];

type ChartRow = Record<string, string | number>;

const findHeaderIndex = (headers: string[], target: string) =>
  headers.findIndex((header) => header.toLowerCase() === target.toLowerCase());

interface AppVersionCardProps {
  startDate?: string;
  endDate?: string;
}

export const AppVersionCard = ({ startDate, endDate }: AppVersionCardProps = {}) => {
  const hasRange = !!startDate && !!endDate;
  const { data, loading, error } = useQuery<AnalyticsReportQuery>(
    hasRange ? activeUsersByAppVersionReportByRange : activeUsersByAppVersionReport,
    hasRange ? { variables: { startDate, endDate } } : undefined
  );
  const report = data?.analyticsReport ?? null;

  const { chartData, versions } = React.useMemo(() => {
    if (!report) return { chartData: [] as ChartRow[], versions: [] as string[] };

    const dateIndex = Math.max(0, findHeaderIndex(report.dimensionHeaders, "date"));
    const versionIndex = Math.max(
      0,
      findHeaderIndex(report.dimensionHeaders, "appVersion")
    );
    const metricIndex = Math.max(
      0,
      findHeaderIndex(report.metricHeaders, "activeUsers")
    );

    const totalsByVersion = report.rows.reduce<Record<string, number>>(
      (acc, row) => {
        const version = row.dimensionValues[versionIndex] || "Unknown";
        const value =
          metricIndex >= 0 ? parseMetricValue(row.metricValues[metricIndex]) : 0;
        acc[version] = (acc[version] ?? 0) + value;
        return acc;
      },
      {}
    );

    const topVersions = Object.entries(totalsByVersion)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([version]) => version);

    const dateMap = new Map<string, { sortValue: number | string; data: ChartRow }>();

    report.rows.forEach((row) => {
      const rawDate = row.dimensionValues[dateIndex] || "";
      const version = row.dimensionValues[versionIndex] || "Unknown";
      if (!topVersions.includes(version)) return;

      const normalizedDate = normalizeDimensionValue(rawDate);
      const sortValue = /^\d{8}$/.test(rawDate) ? Number(rawDate) : normalizedDate;
      const value =
        metricIndex >= 0 ? parseMetricValue(row.metricValues[metricIndex]) : 0;

      const existing = dateMap.get(normalizedDate);
      if (!existing) {
        dateMap.set(normalizedDate, {
          sortValue,
          data: { date: normalizedDate, [version]: value },
        });
      } else {
        const currentValue = existing.data[version];
        const numericValue = typeof currentValue === "number" ? currentValue : 0;
        existing.data[version] = numericValue + value;
      }
    });

    const chartData = Array.from(dateMap.values())
      .sort((a, b) => compareSortValues(a.sortValue, b.sortValue))
      .map((entry) => entry.data);

    return { chartData, versions: topVersions };
  }, [report]);

  if (loading) {
    return (
      <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        Failed to load chart.
      </Typography>
    );
  }

  if (!report || versions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data available.
      </Typography>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" hide />
          <YAxis width={32} />
          <Tooltip />
          <Legend />
          {versions.map((version, index) => (
            <Line
              key={version}
              type="monotone"
              dataKey={version}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
